/**
 * Part Form Dialog Component
 *
 * Dialog for adding new parts or editing existing ones in inventory.
 * Supports all part fields including categorization, pricing, and (on
 * create) an initial stock entry that lands in a real warehouse with a
 * proper, auditable stock movement (#724).
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Autocomplete,
  DialogContent,
  TextField,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  InputAdornment,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Switch,
  FormControlLabel,
} from '@mui/material';
import { FormDialog } from '@/components/common/FormDialog';
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner';
import { useCentsInput } from '@/hooks/useCentsInput';
import type { InventoryItem, Supplier } from '@/types';
import type { CategoryItem, WarehouseItem } from '@/hooks/useInventory';
import { BarcodeScannerDialog } from './BarcodeScannerDialog';

/** Units the backend accepts on `parts.unit` (services/staff-api inventory.schema.ts). */
const UNITS = ['piece', 'liter', 'kg', 'meter', 'set', 'pair', 'box'] as const;

export interface InitialStockInput {
  warehouseId: string;
  quantity: number;
  unitCostCents: number;
  expiryDate: string | null;
  note: string;
}

interface PartFormDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (part: Partial<InventoryItem>, initialStock: InitialStockInput | null) => void;
  part?: InventoryItem | null;
  suppliers: Supplier[];
  categories: CategoryItem[];
  warehouses: WarehouseItem[];
}

export function PartFormDialog({ open, onClose, onSave, part, suppliers, categories, warehouses }: PartFormDialogProps) {
  const { t } = useTranslation('inventory');
  const isEdit = !!part;

  const defaultWarehouseId = warehouses.find((w) => w.isDefault)?.id ?? warehouses[0]?.id ?? '';

  const [formData, setFormData] = useState({
    sku: '',
    name: '',
    description: '',
    tags: [] as string[],
    categoryId: '',
    oemPartNumber: '',
    unit: 'piece',
    supplierId: '',
    unitPriceCents: 0,
    costPriceCents: 0,
    reorderPoint: 5,
    reorderQuantity: 10,
    maxStockLevel: 0,
    isActive: true,
    barcode: '',
  });

  const [initialStock, setInitialStock] = useState({
    warehouseId: '',
    quantity: 0,
    unitCostCents: 0,
    expiryDate: '',
    note: '',
  });

  const [keywordInput, setKeywordInput] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [scannerOpen, setScannerOpen] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Initialize form with part data if editing
  useEffect(() => {
    if (!open) return;
    if (part) {
      setFormData({
        sku: part.partNumber,
        name: part.name,
        description: part.description || '',
        tags: part.tags ?? [],
        categoryId: part.categoryId ?? categories[0]?.id ?? '',
        oemPartNumber: part.oemPartNumber || '',
        unit: part.unit || 'piece',
        supplierId: part.supplierId || '',
        unitPriceCents: part.unitPriceCents,
        costPriceCents: part.costPriceCents,
        reorderPoint: part.minStockLevel,
        reorderQuantity: part.reorderQuantity ?? 10,
        maxStockLevel: part.maxStockLevel || 0,
        isActive: part.isActive ?? true,
        barcode: part.barcode || '',
      });
    } else {
      // Reset for new part
      setFormData({
        sku: '',
        name: '',
        description: '',
        tags: [],
        categoryId: categories[0]?.id ?? '',
        oemPartNumber: '',
        unit: 'piece',
        supplierId: '',
        unitPriceCents: 0,
        costPriceCents: 0,
        reorderPoint: 5,
        reorderQuantity: 10,
        maxStockLevel: 0,
        isActive: true,
        barcode: '',
      });
      setInitialStock({
        warehouseId: defaultWarehouseId,
        quantity: 0,
        unitCostCents: 0,
        expiryDate: '',
        note: '',
      });
    }
    setKeywordInput('');
    setErrors({});
    setIsDirty(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [part, open]);

  const handleChange = (field: string, value: unknown) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleInitialStockChange = (field: keyof typeof initialStock, value: unknown) => {
    setInitialStock((prev) => ({ ...prev, [field]: value }));
    setIsDirty(true);
  };

  const priceResetKey = open ? (part?.id ?? 'new') : 'closed';
  const salePriceInput = useCentsInput(
    formData.unitPriceCents,
    (cents) => handleChange('unitPriceCents', cents),
    priceResetKey,
  );
  const costPriceInput = useCentsInput(
    formData.costPriceCents,
    (cents) => {
      handleChange('costPriceCents', cents);
      // The initial-stock unit cost defaults to the part's cost price until
      // the user overrides it directly.
      if (!isEdit) {
        setInitialStock((prev) => ({ ...prev, unitCostCents: cents }));
      }
    },
    priceResetKey,
  );
  const initialUnitCostInput = useCentsInput(
    initialStock.unitCostCents,
    (cents) => handleInitialStockChange('unitCostCents', cents),
    priceResetKey,
  );

  const handleScanBarcode = () => {
    setScannerOpen(true);
  };

  const handleBarcodeScanned = (barcode: string) => {
    handleChange('barcode', barcode);
  };

  const validate = (unitPriceCents: number, costPriceCents: number) => {
    const newErrors: Record<string, string> = {};

    if (!formData.name.trim()) {
      newErrors.name = t('partForm.nameRequired');
    }
    if (!formData.categoryId) {
      newErrors.categoryId = t('partForm.categoryRequired');
    }
    if (unitPriceCents < 0) {
      newErrors.unitPriceCents = t('partForm.priceMustBePositive');
    }
    if (costPriceCents < 0) {
      newErrors.costPriceCents = t('partForm.costMustBePositive');
    }
    if (formData.reorderPoint < 0) {
      newErrors.reorderPoint = t('partForm.minStockMustBePositive');
    }
    if (!isEdit && initialStock.quantity < 0) {
      newErrors.initialQuantity = t('partForm.quantityMustBePositive');
    }
    if (!isEdit && initialStock.quantity > 0 && !initialStock.warehouseId) {
      newErrors.initialWarehouseId = t('partForm.warehouseRequired');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const normalizeTags = (tags: string[]) => {
    const trimmed = tags.map((tag) => tag.trim()).filter(Boolean);
    return trimmed.filter((tag, index) =>
      trimmed.findIndex((candidate) => candidate.toLowerCase() === tag.toLowerCase()) === index);
  };

  const handleSubmit = () => {
    const unitPriceCents = salePriceInput.commit(salePriceInput.value);
    const costPriceCents = costPriceInput.commit(costPriceInput.value);
    const unitCostCents = initialUnitCostInput.commit(initialUnitCostInput.value);
    if (!validate(unitPriceCents, costPriceCents)) return;
    const tags = normalizeTags([...formData.tags, keywordInput]);
    if (tags.length > 50 || tags.some((tag) => tag.length > 100)) {
      setErrors((prev) => ({ ...prev, tags: t('partForm.keywordsLimit') }));
      return;
    }

    // A blank SKU is fine — the backend requires one, so generate a
    // reasonably unique one rather than blocking the user (#724).
    const sku = formData.sku.trim() || `SKU-${String(Date.now())}`;

    onSave(
      { ...formData, tags, partNumber: sku, unitPriceCents, costPriceCents },
      !isEdit && initialStock.quantity > 0
        ? {
            warehouseId: initialStock.warehouseId,
            quantity: initialStock.quantity,
            unitCostCents,
            expiryDate: initialStock.expiryDate || null,
            note: initialStock.note.trim(),
          }
        : null,
    );
    onClose();
  };

  return (
    <FormDialog
      open={open}
      onClose={onClose}
      title={isEdit ? t('partForm.editTitle') : t('partForm.addTitle')}
      isDirty={isDirty}
      onSubmit={handleSubmit}
      submitLabel={isEdit ? t('partForm.saveChanges') : t('partForm.addPart')}
      maxWidth="md"
    >
      <DialogContent dividers>
        {isEdit && (
          <Alert severity="info" sx={{ mb: 3 }}>
            {t('partForm.editingPart', { partNumber: part?.partNumber })}
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Basic Information */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom>
              {t('partForm.basicInfo')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('partForm.partNumberLabel')}
              value={formData.sku}
              onChange={(e) => handleChange('sku', e.target.value)}
              fullWidth
              autoFocus
              helperText={t('partForm.skuAutoGenerateHint')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('partForm.barcodeLabel')}
              value={formData.barcode}
              onChange={(e) => handleChange('barcode', e.target.value)}
              fullWidth
              slotProps={{
                input: {
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={t('barcode.scanTitle')}>
                        <IconButton onClick={handleScanBarcode} edge="end" aria-label={t('barcode.scanTitle')}>
                          <QrCodeScannerIcon />
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('partForm.partName')}
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              required
              fullWidth
              error={!!errors.name}
              helperText={errors.name}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <TextField
              label={t('partForm.descriptionLabel')}
              value={formData.description}
              onChange={(e) => handleChange('description', e.target.value)}
              fullWidth
              multiline
              rows={3}
            />
          </Grid>

          <Grid size={{ xs: 12 }}>
            <Autocomplete
              multiple
              freeSolo
              autoSelect
              options={[] as string[]}
              value={formData.tags}
              inputValue={keywordInput}
              onInputChange={(_, value) => { setKeywordInput(value); if (value) setIsDirty(true); }}
              onChange={(_, tags) => handleChange('tags', normalizeTags(tags))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label={t('partForm.keywords')}
                  helperText={errors.tags || t('partForm.keywordsHint')}
                  error={!!errors.tags}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth required error={!!errors.categoryId}>
              <InputLabel>{t('partForm.categoryLabel')}</InputLabel>
              <Select
                value={formData.categoryId}
                label={t('partForm.categoryLabel')}
                onChange={(e) => handleChange('categoryId', e.target.value)}
              >
                {categories.map((category) => (
                  <MenuItem key={category.id} value={category.id}>
                    {category.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControl fullWidth>
              <InputLabel>{t('partForm.unitLabel')}</InputLabel>
              <Select
                value={formData.unit}
                label={t('partForm.unitLabel')}
                onChange={(e) => handleChange('unit', e.target.value)}
              >
                {UNITS.map((unit) => (
                  <MenuItem key={unit} value={unit}>
                    {t(`partForm.units.${unit}`)}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('partForm.oemPartNumberLabel')}
              value={formData.oemPartNumber}
              onChange={(e) => handleChange('oemPartNumber', e.target.value)}
              fullWidth
              helperText={t('partForm.oemPartNumberHint')}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <FormControlLabel
              sx={{ mt: 1 }}
              control={
                <Switch
                  checked={formData.isActive}
                  onChange={(e) => handleChange('isActive', e.target.checked)}
                />
              }
              label={t('partForm.isActiveLabel')}
            />
          </Grid>

          {/* Supplier */}
          <Grid size={{ xs: 12 }}>
            <FormControl fullWidth>
              <InputLabel>{t('partForm.supplierLabel')}</InputLabel>
              <Select
                value={formData.supplierId}
                label={t('partForm.supplierLabel')}
                onChange={(e) => handleChange('supplierId', e.target.value)}
              >
                <MenuItem value="">
                  <em>{t('partForm.noSupplier')}</em>
                </MenuItem>
                {suppliers.map((supplier) => (
                  <MenuItem key={supplier.id} value={supplier.id}>
                    {supplier.name}
                    {supplier.deliveryDays && ` ${t('partForm.deliveryDaysShort', { days: supplier.deliveryDays })}`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Pricing */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 1 }}>
              {t('partForm.pricingSection')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('partForm.salePriceLabel')}
              type="number"
              value={salePriceInput.value}
              onChange={salePriceInput.onChange}
              onFocus={salePriceInput.onFocus}
              onBlur={salePriceInput.onBlur}
              required
              fullWidth
              error={!!errors.unitPriceCents}
              helperText={errors.unitPriceCents || t('partForm.salePriceHint')}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                },
                htmlInput: {
                  step: 0.01,
                  min: 0,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <TextField
              label={t('partForm.costPriceLabel')}
              type="number"
              value={costPriceInput.value}
              onChange={costPriceInput.onChange}
              onFocus={costPriceInput.onFocus}
              onBlur={costPriceInput.onBlur}
              required
              fullWidth
              error={!!errors.costPriceCents}
              helperText={errors.costPriceCents || t('partForm.costPriceHint')}
              slotProps={{
                input: {
                  startAdornment: <InputAdornment position="start">€</InputAdornment>,
                },
                htmlInput: {
                  step: 0.01,
                  min: 0,
                },
              }}
            />
          </Grid>

          {/* Reorder levels */}
          <Grid size={{ xs: 12 }}>
            <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 1 }}>
              {t('partForm.stockSection')}
            </Typography>
            <Divider sx={{ mb: 2 }} />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label={t('partForm.minLevel')}
              type="number"
              value={formData.reorderPoint}
              onChange={(e) => handleChange('reorderPoint', parseInt(e.target.value, 10) || 0)}
              required
              fullWidth
              error={!!errors.reorderPoint}
              helperText={errors.reorderPoint || t('partForm.minLevelHint')}
              slotProps={{
                htmlInput: {
                  min: 0,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label={t('partForm.reorderQuantityLabel')}
              type="number"
              value={formData.reorderQuantity}
              onChange={(e) => handleChange('reorderQuantity', parseInt(e.target.value, 10) || 0)}
              fullWidth
              helperText={t('partForm.reorderQuantityHint')}
              slotProps={{
                htmlInput: {
                  min: 0,
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label={t('partForm.maxLevel')}
              type="number"
              value={formData.maxStockLevel}
              onChange={(e) => handleChange('maxStockLevel', parseInt(e.target.value, 10) || 0)}
              fullWidth
              helperText={t('partForm.maxLevelHint')}
              slotProps={{
                htmlInput: {
                  min: 0,
                },
              }}
            />
          </Grid>

          {/* Initial stock — only meaningful when creating a brand new part */}
          {!isEdit && (
            <>
              <Grid size={{ xs: 12 }}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom sx={{ mt: 1 }}>
                  {t('partForm.initialStockSection')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('partForm.initialStockHint')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <FormControl fullWidth error={!!errors.initialWarehouseId}>
                  <InputLabel>{t('partForm.warehouseLabel')}</InputLabel>
                  <Select
                    value={initialStock.warehouseId}
                    label={t('partForm.warehouseLabel')}
                    onChange={(e) => handleInitialStockChange('warehouseId', e.target.value)}
                  >
                    {warehouses.map((warehouse) => (
                      <MenuItem key={warehouse.id} value={warehouse.id}>
                        {warehouse.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('partForm.initialQuantityLabel')}
                  type="number"
                  value={initialStock.quantity}
                  onChange={(e) => handleInitialStockChange('quantity', parseInt(e.target.value, 10) || 0)}
                  fullWidth
                  error={!!errors.initialQuantity}
                  helperText={errors.initialQuantity || errors.initialWarehouseId}
                  slotProps={{
                    htmlInput: { min: 0 },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('partForm.initialUnitCostLabel')}
                  type="number"
                  value={initialUnitCostInput.value}
                  onChange={initialUnitCostInput.onChange}
                  onFocus={initialUnitCostInput.onFocus}
                  onBlur={initialUnitCostInput.onBlur}
                  fullWidth
                  slotProps={{
                    input: {
                      startAdornment: <InputAdornment position="start">€</InputAdornment>,
                    },
                    htmlInput: { step: 0.01, min: 0 },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <TextField
                  label={t('partForm.initialExpiryDateLabel')}
                  type="date"
                  value={initialStock.expiryDate}
                  onChange={(e) => handleInitialStockChange('expiryDate', e.target.value)}
                  fullWidth
                  slotProps={{
                    inputLabel: { shrink: true },
                  }}
                />
              </Grid>

              <Grid size={{ xs: 12 }}>
                <TextField
                  label={t('partForm.initialNoteLabel')}
                  value={initialStock.note}
                  onChange={(e) => handleInitialStockChange('note', e.target.value)}
                  fullWidth
                  placeholder={t('partForm.initialNotePlaceholder')}
                />
              </Grid>
            </>
          )}
        </Grid>
      </DialogContent>

      <BarcodeScannerDialog
        open={scannerOpen}
        onClose={() => setScannerOpen(false)}
        onScan={handleBarcodeScanned}
      />
    </FormDialog>
  );
}

export { UNITS as PART_UNITS };
